import EventEmitter from 'events';
import { current, produce, original } from 'immer';
import { clamp, max, trim } from 'lodash';
import { Nullable } from 'src/types/nullable.type';
import { blockGridToBlockList } from '../helpers/block/block-grid-to-block-list';
import { blocksToBlockGrid } from '../helpers/block/blocks-to-block-grid';
import { findBlock } from '../helpers/find-block';
import { generateBlockId } from '../helpers/generate-block-id';
import { getSuperRect } from '../helpers/get-super-rect';
import { cloneDeep } from 'lodash';
import {
  AspectRatio,
  AutoHeightSize,
  Block,
  BlockContent,
  BlockGrid,
  BlockLayoutSpec,
  BlockRenderElement,
  BlockVerticalAlignment,
  Boundary,
  BoundaryParams,
  BoundarySpace,
  CalculatedHeightSize,
  LayoutMode,
  Rect,
  RenderElement,
  Section,
  SectionGrid,
  Size,
  StaticSize,
} from '../types';
import { calculateWidth } from './calculate-width';
import { getWidthPercentage } from './get-width-percentage';
import { locateBlockInGrid } from './locate-block-in-grid';

export type PlayerLayoutConfig = {
  margin: {
    top: number;
    bottom: number;
  };
  block: {
    maxHeight: number;
    defaultHeight: number;
    defaultAspectRatio: {
      width: number;
      height: number;
    };
    minWidth: number;
  };
  row: {
    minHeight: number;
  };
  rowGap: number;
  columnGap: number;
  sectionGap: number;
};

export const DEFAULT_PLAYER_LAYOUT_CONFIG: PlayerLayoutConfig = {
  margin: {
    top: 100,
    bottom: 100,
  },
  block: {
    maxHeight: 400,
    defaultHeight: 400,
    defaultAspectRatio: {
      width: 16,
      height: 9,
    },
    minWidth: 160,
  },
  row: {
    minHeight: 40,
  },
  rowGap: 32,
  columnGap: 32,
  sectionGap: 80,
} as const;

const DEFAULT_PLAYER_MOBILE_LAYOUT_CONFIG: PlayerLayoutConfig = {
  ...cloneDeep(DEFAULT_PLAYER_LAYOUT_CONFIG),
  margin: {
    top: 56,
    bottom: 100,
  },
  rowGap: 16,
  columnGap: 16,
  sectionGap: 80,
};

function calculateRowBlockSizes(
  row: Block[],
  rowWidth: number,
  layoutConfig: PlayerLayoutConfig,
  layoutMode: LayoutMode
): { height: number; blockSizes: Size[] } {
  if (layoutMode === 'web' && row.length > 1) {
    rowWidth -= layoutConfig.columnGap * (row.length - 1);
  }

  let blockSizes: Size[] = [];
  let rowHeight = 0;
  for (let i = 0; i < row.length; i++) {
    const block = row[i];
    const spec = block.layout.spec;
    let width = 0;
    let height = 0;
    if (layoutMode === 'web') {
      if (spec.type === 'static') {
        width = calculateWidth(spec.width, rowWidth);
        height = spec.height || layoutConfig.block.defaultHeight;
      } else if (spec.type === 'auto-height') {
        width = calculateWidth(spec.width, rowWidth);
        height =
          spec.height ||
          Math.min(Math.floor((spec.aspectRatio.height / spec.aspectRatio.width) * width), spec.maxHeight);
      } else if (spec.type === 'calculated-height') {
        width = calculateWidth(spec.width, rowWidth);
        height = Math.max(spec.height || spec.minHeight, spec.contentSize?.height || 0);
      }
    } else {
      if (spec.type === 'static') {
        width = rowWidth;
        height = spec.height || layoutConfig.block.defaultHeight;
      } else if (spec.type === 'auto-height') {
        width = rowWidth;
        height =
          spec.height ||
          Math.min(Math.floor((spec.aspectRatio.height / spec.aspectRatio.width) * width), spec.maxHeight);
      } else if (spec.type === 'calculated-height') {
        width = rowWidth;
        height = Math.max(spec.height || spec.minHeight, spec.contentSize?.height || 0);
      }
    }

    rowHeight = Math.max(rowHeight, height);
    blockSizes.push({ width, height });
  }
  return { height: rowHeight, blockSizes: blockSizes.map((bs) => ({ ...bs, height: rowHeight })) };
}
export type BlockLayoutExtraParams = Partial<
  Omit<StaticSize, 'type' | 'width'> & Omit<AutoHeightSize, 'type'> & Omit<CalculatedHeightSize, 'type'>
>;

export type PlayerLayoutInputs = {
  innerAreaWidth: number;
  innerAreaHeight: number;
};

export type PlayerLayoutOutputs = {
  gridWidth: number;
  gridHeight: number;
  topOffset: number;
  bottomOffset: number;
  innerActualWidth: number;
  innerActualHeight: number;
  sectionRects: Rect[];
};

export class PlayerLayoutManager extends EventEmitter {
  sections: Section[] = [];
  sectionGrids: SectionGrid[] = [];
  renderElements: BlockRenderElement[] = [];
  webLayoutConfig: PlayerLayoutConfig;
  mobileLayoutConfig: PlayerLayoutConfig;
  layoutMode: LayoutMode = 'web';
  inputs: Nullable<PlayerLayoutInputs> = null;
  outputs: Nullable<PlayerLayoutOutputs> = null;
  constructor() {
    super();
    this.webLayoutConfig = DEFAULT_PLAYER_LAYOUT_CONFIG;
    this.mobileLayoutConfig = DEFAULT_PLAYER_MOBILE_LAYOUT_CONFIG;
  }
  setLayoutMode(layoutMode: LayoutMode) {
    console.log('plm setLayoutMode', layoutMode);
    this.layoutMode = layoutMode;
    this._updateSectionGrids();
    this._invalidate();
  }
  initializeSections(sections: Section[]) {
    this.sections = sections;
    this._updateSectionGrids();
    this._invalidate();
  }
  reset() {
    this.sections = [];
    this.sectionGrids = [];
    this.renderElements = [];
    this.outputs = null;
  }
  setLayoutInputs(inputs: PlayerLayoutInputs) {
    console.log('plm setLayoutInputs', inputs);
    this.inputs = inputs;
    this._updateSectionGrids();
    this._invalidate();
  }
  setBlockContentSize(id: Block['id'], size: Nullable<Size>) {
    console.log('plm setBlockContentSize', { ...size });
    this.sections = produce(this.sections, (sections) => {
      const block = findBlock(sections, id);
      if (block && block.layout.spec.type === 'calculated-height') {
        block.layout.spec.contentSize = size;
      }
    });
    this._updateSectionGrids();
    this._invalidate();
  }
  _updateSectionGrids() {
    const { inputs, layoutMode } = this;
    if (!inputs) {
      return;
    }
    this.sectionGrids = this.sections.map((section) => ({
      id: section.id,
      name: section.name,
      blockGrid: blocksToBlockGrid(section.blocks, inputs.innerAreaWidth, layoutMode),
    }));
  }
  _invalidate() {
    console.log('plm _invalidate');
    const { layoutMode } = this;
    if (layoutMode === 'web') {
      this._layoutWeb();
    } else if (layoutMode === 'mobile-portrait' || layoutMode === 'mobile-landscape') {
      this._layoutMobile();
    }
    this.emit('update');
  }
  _layoutWeb() {
    const { sectionGrids, inputs } = this;
    if (!inputs) {
      return;
    }
    console.log('plm _layoutWeb');
    const {
      rowGap: GRID_ROW_GAP,
      columnGap: GRID_COLUMN_GAP,
      margin: { top: MARGIN_TOP, bottom: MARGIN_BOTTOM },
      sectionGap: SECTION_GAP,
    } = this.webLayoutConfig;
    const { innerAreaWidth, innerAreaHeight } = inputs;

    const gridWidth = innerAreaWidth;

    let x = 0;
    let y = 0;
    let gridHeight = 0;

    let renderElements: BlockRenderElement[] = [];
    let sectionRects: Rect[] = [];

    for (let k = 0; k < sectionGrids.length; k++) {
      const sectionGrid = sectionGrids[k];
      const blockGrid = sectionGrid.blockGrid;
      let sectionHeight = 0;
      let sectionY = y;
      for (let i = 0; i < blockGrid.length; i++) {
        const row = blockGrid[i];
        const { height: rowHeight, blockSizes } = calculateRowBlockSizes(
          row,
          gridWidth,
          this.webLayoutConfig,
          this.layoutMode
        );
        for (let j = 0; j < row.length; j++) {
          const block = row[j];
          const blockSize = blockSizes[j]!;
          renderElements.push({
            type: 'block',
            block,
            rect: { x, y, width: blockSize.width, height: blockSize.height },
          });
          x += blockSize.width + GRID_COLUMN_GAP;
        }
        sectionHeight += rowHeight;
        y += rowHeight;
        if (i < blockGrid.length - 1) {
          sectionHeight += GRID_ROW_GAP;
          y += GRID_ROW_GAP;
        }
        x = 0;
      }
      sectionRects.push({
        x: 0,
        y: sectionY,
        width: gridWidth,
        height: sectionHeight,
      });
      gridHeight += sectionHeight;
      if (k < sectionGrids.length - 1) {
        y += SECTION_GAP;
        gridHeight += SECTION_GAP;
      }
    }

    const topOffset = Math.max(MARGIN_TOP, (innerAreaHeight - gridHeight) / 2);
    const bottomOffset = Math.max(MARGIN_BOTTOM, (innerAreaHeight - gridHeight) / 2);

    const innerActualHeight = gridHeight + topOffset + bottomOffset;

    renderElements = produce(renderElements, (renderElements) => {
      for (const renderElement of renderElements) {
        renderElement.rect.y += topOffset;
      }
    });
    this.outputs = {
      gridHeight,
      gridWidth,
      topOffset,
      bottomOffset,
      innerActualWidth: innerAreaWidth,
      innerActualHeight,
      sectionRects,
    };
    this.renderElements = renderElements;
  }
  _layoutMobile() {
    const { sectionGrids, inputs } = this;
    if (!inputs) {
      return;
    }
    console.log('plm _layoutMobile');
    const {
      rowGap: GRID_ROW_GAP,
      columnGap: GRID_COLUMN_GAP,
      margin: { top: MARGIN_TOP, bottom: MARGIN_BOTTOM },
      sectionGap: SECTION_GAP,
    } = this.mobileLayoutConfig;
    const { innerAreaWidth, innerAreaHeight } = inputs;

    const gridWidth = innerAreaWidth;

    let x = 0;
    let y = 0;
    let gridHeight = 0;

    let renderElements: BlockRenderElement[] = [];
    let sectionRects: Rect[] = [];

    for (let k = 0; k < sectionGrids.length; k++) {
      const sectionGrid = sectionGrids[k];
      const blockGrid = sectionGrid.blockGrid;
      let sectionHeight = 0;
      let sectionY = y;
      for (let i = 0; i < blockGrid.length; i++) {
        const row = blockGrid[i];
        const { height: rowHeight, blockSizes } = calculateRowBlockSizes(
          row,
          gridWidth,
          this.mobileLayoutConfig,
          this.layoutMode
        );
        for (let j = 0; j < row.length; j++) {
          const block = row[j];
          const blockSize = blockSizes[j]!;
          renderElements.push({
            type: 'block',
            block,
            rect: { x, y, width: blockSize.width, height: blockSize.height },
          });
          x += blockSize.width + GRID_COLUMN_GAP;
        }
        sectionHeight += rowHeight;
        y += rowHeight;
        if (i < blockGrid.length - 1) {
          sectionHeight += GRID_ROW_GAP;
          y += GRID_ROW_GAP;
        }
        x = 0;
      }
      sectionRects.push({
        x: 0,
        y: sectionY,
        width: gridWidth,
        height: sectionHeight,
      });
      gridHeight += sectionHeight;
      if (k < sectionGrids.length - 1) {
        y += SECTION_GAP;
        gridHeight += SECTION_GAP;
      }
    }

    const topOffset = Math.max(MARGIN_TOP, (innerAreaHeight - gridHeight) / 2);
    const bottomOffset = Math.max(MARGIN_BOTTOM, (innerAreaHeight - gridHeight) / 2);

    const innerActualHeight = gridHeight + topOffset + bottomOffset;

    renderElements = produce(renderElements, (renderElements) => {
      for (const renderElement of renderElements) {
        renderElement.rect.y += topOffset;
      }
    });
    this.outputs = {
      gridHeight,
      gridWidth,
      topOffset,
      bottomOffset,
      innerActualWidth: innerAreaWidth,
      innerActualHeight,
      sectionRects,
    };
    this.renderElements = renderElements;
  }
}
